<template>
    <div class="h-full jupyter-container">
        <AppLoader :force-loading="jupyterLoading" default-height="50vh" />
        <iframe
            ref="jupyterFrame"
            :src="jupyterUrl"
            class="jupyter-iframe w-full"
            :class="message ? 'h-[calc(100vh-10.5rem)]' : 'h-[calc(100vh-6.5rem)]'"></iframe>
        <div v-if="message" class="rounded-md bg-yellow-50 p-4 my-4">
            <div class="flex">
                <div class="text-xs text-yellow-700 flex gap-2">
                    <div class="shrink-0">
                        <ExclamationTriangleIcon class="size-5 text-yellow-400" aria-hidden="true" />
                    </div>
                    <p class="text-xs">{{ message }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import AppLoader from '../../components/AppLoader.vue';
import { ref, onMounted } from 'vue';
import { cloneDeep, set as deepSet, debounce } from 'lodash';
import { ExclamationTriangleIcon } from '@heroicons/vue/20/solid';
import { useTransformsStore } from '@/stores/transforms.store';
import Config from '../../utils/config';
import { method } from 'lodash';
const ApiDomain = Config.app.apiUrl.endsWith('/') ? Config.app.apiUrl : `${Config.app.apiUrl}/`;

const props = defineProps({
    transformId: {
        type: String,
        required: true
    }
});

const transformsStore = useTransformsStore();

const jupyterUrl = '/jupyterlite/lab/index.html'; // Self-hosted JupyterLite

const jupyterLoading = ref(false);
const message = ref('');
const jupyterFrame = ref(null);
const implementation_details = ref(null);
const jupyterAppRef = ref(null);
let saveAllFilesDebounced;
let waitForJupyterRetryCount = 0;

// Reloads file information from the server and then inserts them into JupyterLite.
async function reloadFiles() {
    message.value = 'Loading files...';
    try {
        const implementation_versions = await transformsStore.getImplementationDetails(props.transformId);
        const latest_version = Object.keys(implementation_versions.impl_versions).sort().reverse()[0];
        const impl_details = implementation_versions.impl_versions[latest_version];
        implementation_details.value = impl_details;

        setTimeout(() => loadFilesIntoJupyterLite(impl_details), 1000);

        message.value = '';
    } catch (error) {
        console.error('Error loading transform_code files:', error);
        message.value = 'Failed to load transform_code files!';
    }
}

// Applies custom settings to the JupyterLite iframe.
async function applyStreamkapCustomSettings() {
    try {
        const iframe = jupyterFrame.value;
        console.log('XXX', iframe, iframe?.contentWindow?.document);
        if (!iframe?.contentWindow?.document?.getElementById('jp-main-content-panel')) {
            if (waitForJupyterRetryCount > 25) {
                throw new Error('JupyterLite not loaded!');
            }
            waitForJupyterRetryCount++;
            setTimeout(() => applyStreamkapCustomSettings(), 500);
            return;
        }
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.getElementById('jp-top-panel').remove();
        iframeDoc.getElementById('jp-bottom-panel').remove();
        iframeDoc.getElementById('jp-main-content-panel').style.top = 0;

        const styleElem = iframeDoc.createElement('style');
        styleElem.id = 'streamkap-jupyter-lite-styles';
        styleElem.textContent = `
          .jp-Cell {
              max-height: 250px; /* Set the maximum height for cells */
              overflow-y: auto; /* Enable vertical scrolling for overflow */
          }
      `;

        jupyterLoading.value = false;
    } catch (error) {
        console.error('Error applying custom settings:', error);
        jupyterLoading.value = false;
    }

    // iframeDoc.head.appendChild(styleElem);
}

// Intercepts the save command within JupyterLite to perform custom logic.
async function interceptOnSave(jupyterApp) {
    const commands = jupyterApp.commands;
    console.log('Intercepting Save command');
    const originalSaveCommand = commands._commands.get('docmanager:save');
    console.log('Original Save command', originalSaveCommand);

    const originalExecute = originalSaveCommand.execute;
    originalSaveCommand.execute = async (args) => {
        console.log('Intercepted Save command!', args);
        message.value = 'Custom save logic executed!';
        await saveAllFilesDebounced(jupyterApp.serviceManager.contents);
        if (originalExecute) {
            await originalExecute.call(originalSaveCommand, args);
        }
        console.log('Save completed!');
    };

    console.log('Save command intercepted successfully!');
}

function loadFilesFromObject(parentPath, obj) {
    const files = [];
    for (const key in obj) {
        if (key.match(/\.(js|py|sql|json|md|txt|csv|ipynb)$/)) {
            var content = obj[key];
            console.log('loadFilesFromObject', key, content);
            if (key.match(/\.json$/)) {
                try {
                    content = JSON.stringify(content, null, 2);
                } catch (e) {
                    console.error('Error pretty printing JSON:', e);
                }
            }
            files.push({ path: parentPath + '/' + key, content: content });
        } else if (key.match(/(__json)$/)) {
            var content = obj[key];
            var fileName = key.replace(/__json$/, '.json.js');
            try {
                content = JSON.stringify(content, null, 2);
            } catch (e) {
                console.error('Error pretty printing JSON:', e);
            }
            files.push({ path: parentPath + '/' + fileName, content: content });
        } else if (key.match(/(value|key|topics)_transform$/)) {
            var content = obj[key];
            var fileName = key.replace(/_transform$/, 'Transform.js');
            files.push({ path: parentPath + '/' + fileName, content: content });
        } else if (typeof obj[key] === 'object') {
            const nestedFiles = loadFilesFromObject(parentPath + '/' + key, obj[key]);
            files.push(...nestedFiles);
        }
        if (key === 'main_notebook') {
            files.push({ path: parentPath + '/MAIN.ipynb', content: obj[key] });
        }
    }

    return files;
}

// Inserts the main notebook, tests, and code files into JupyterLite.
async function loadFilesIntoJupyterLite(impl_details) {
    const jupyterApp = jupyterFrame.value?.contentWindow?.jupyterapp;
    const docManager = jupyterApp?.serviceManager?.contents;
    if (!docManager) {
        if (waitForJupyterRetryCount > 25) {
            throw new Error('JupyterLite not loaded!');
        }
        waitForJupyterRetryCount++;
        setTimeout(() => loadFilesIntoJupyterLite(impl_details), 1000);
        return;
    }

    jupyterAppRef.value = jupyterApp;

    console.log('Loading files in jupyter-lite', impl_details);
    try {
        const files = loadFilesFromObject('', impl_details);
        console.log('Files to load:', files);

        var mandatoryFilePrefixes = {
            '/MAIN.ipynb': false,
            '/transform_code/valueTransform': false
        };

        for (const file of files) {
            await loadFile(docManager, file.path, file.content);
            for (const prefix in mandatoryFilePrefixes) {
                if (file.path.startsWith(prefix)) {
                    mandatoryFilePrefixes[prefix] = true;
                }
            }
        }

        for (const prefix in mandatoryFilePrefixes) {
            if (mandatoryFilePrefixes[prefix] == false) {
                message.value = `Missing mandatory file on loading: ${prefix}`;
                throw new Error(message.value);
            }
        }

        message.value = `Files loaded successfully ... opening MAIN.ipynb`;

        await jupyterApp.commands.execute('docmanager:open', {
            path: 'MAIN.ipynb'
        });
        message.value = `MAIN.ipynb opened successfully ...`;

        await jupyterApp.commands.execute('docmanager:open', {
            path: '/transform_code/valueTransform.js' // TODO: support python and SQL
        });
        message.value = `valueTransform opened successfully ... `;

        await jupyterApp.commands.execute('docmanager:open', {
            path: '/unit_test/input.json.js'
        });
        message.value = `input.json.js opened successfully ... `;

        await jupyterApp.commands.execute('docmanager:open', {
            path: '/unit_test/output.json.js'
        });
        message.value = `output.json.js opened successfully ... `;

        await jupyterApp.commands.execute('docmanager:open', {
            path: 'MAIN.ipynb'
        });
        message.value = `MAIN.ipynb opened successfully ... loading streamkap utilities`;

        await insertStreamkapUtilities(jupyterApp);

        message.value = `streamkap utilities loaded successfully!`;
    } catch (error) {
        console.error('Error loading file:', error);
        message.value = `Failed to load files!`;
    }
}

// Saves a file to JupyterLite after ensuring its directory exists.
async function loadFile(docManager, fileNameWithPath, contents) {
    console.log('loadFile ', fileNameWithPath, contents);
    await loadDirectoryForFile(docManager, fileNameWithPath);
    let type = 'file';
    let format = 'text';
    if (fileNameWithPath.endsWith('.js')) {
        type = 'file';
        format = 'text';
    }
    if (fileNameWithPath.endsWith('.ipynb')) {
        type = 'notebook';
        format = 'json';
    }
    await docManager.save(fileNameWithPath, {
        type,
        format,
        content: contents
    });
}

// Creates directory structure needed for a file path.
async function loadDirectoryForFile(docManager, fileNameWithPath) {
    const pathParts = fileNameWithPath.replace(/^[/]+/, '').split('/');
    console.log('loadDirectoryForFile ', fileNameWithPath, pathParts.slice(0, -1));
    let parentPath = '';
    for (const folderName of pathParts.slice(0, -1)) {
        const m = await docManager.newUntitled({
            path: '',
            type: 'directory'
        });
        const folderPath = parentPath + '/' + folderName;
        console.log('loadDirectoryForFile ', parentPath, folderName, folderPath);
        await docManager.rename(m.path, folderPath);
        parentPath = folderPath;
    }
}

// Inserts Streamkap utilities into the JupyterLite environment.
async function insertStreamkapUtilities(jupyterApp) {
    console.log('Loading utilities ', jupyterApp);
    const js_code = `
        self.streamkap = {
            ApiDomain: "${ApiDomain}",
            TransformId: "${props.transformId}",
            load_file: async function load_file(fileName) {
                return new Promise((resolve, reject) => {
                    const request = indexedDB.open("JupyterLite Storage");
                    request.onsuccess = function(event) {
                        const db = event.target.result;
                        const transaction = db.transaction("files", "readonly");
                        const objectStore = transaction.objectStore("files");
                        const request = objectStore.get(fileName);
                        request.onsuccess = function(event) {
                            resolve(request.result);
                        };
                        request.onerror = function(event) {
                            reject(request.error);
                        };
                    };
                    request.onerror = function(event) {
                        reject(request.error);
                    };
                });
            },
            msg_from_app: {},
            start: function start(start_msg) {
                console.log(new Date().toISOString() + " | " + start_msg);
            },
            finish: function finish(fun) {
                var msg = streamkap.msg_from_app[fun] || "";
                if (!msg) {
                    msg = "ERROR: " + fun + " no return"; 
                }
                var logMsg = msg;
                if (msg.startsWith('ERROR')) {
                    console.error(logMsg);
                } else {
                    console.log(logMsg);
                }
            },            
            put_msg: function put_msg(key, msg) {
                streamkap.msg_from_app[key] = new Date().toISOString() + " | " + msg;
            },
            append_msg: function append_msg(key, msg) {
                var prev = streamkap.msg_from_app[key];
                if (!prev) {
                    prev = "";
                }
                if (prev.length > 0) {
                    prev += "\\n";
                }
                streamkap.msg_from_app[key] = prev + new Date().toISOString() + " | " + msg;
            },            
            load_json: async function load_json(fileName) {
                const fileContent = await self.streamkap.load_file(fileName);
                return fileContent.content;
            },
            load_js: async function load_js(fileName) {
                const fileContent = await self.streamkap.load_file(fileName);
                return eval(fileContent.content);
            },
            set_in_out_topics: function set_in_out_topics(pattern, replacement) {
                streamkap.start('in_out_topics | ' + pattern + ' | ' + replacement);
            },
            check_in_out_topics: function check_output_topics() {
                streamkap.finish('in_out_topics');
            },
            validate_unit_test_data: async function validate_unit_test_data() {
                try {
                    var inputJsonFile = await streamkap.load_file("unit_test/input.json.js");
                    var inputJson = JSON.parse(inputJsonFile.content);
                    if (!inputJson.topic) throw new Error("intput.json: topic not set");
                
                    var outputJsonFile = await streamkap.load_file("unit_test/output.json.js");
                    var outputJson = JSON.parse(outputJsonFile.content);
                    if (!outputJson.topic) throw new Error("output.json.js: topic not set");
                } catch (e) {
                    console.error(e);
                    return;
                }
                console.info("Validation successful");
            },
            validate_code: async function validate_code() {
                try {
                    var inputJsonFile = await streamkap.load_file("unit_test/input.json.js");
                    var inputJson = JSON.parse(inputJsonFile.content);
                    
                    var outputJsonFile = await streamkap.load_file("unit_test/output.json.js");
                    var outputJson = JSON.parse(outputJsonFile.content);
                
                    var valueTransformFile = await streamkap.load_file("transform_code/valueTransform.js");
                    var valueTransformJs = valueTransformFile.content;
                    eval(valueTransformJs);
                    
                    var inputRecordObj = inputJson.value;
                    var inputObj = (inputRecordObj.schema && inputRecordObj.payload) ? inputRecordObj.payload : inputRecordObj;
                    var outputObj = _streamkap_transform(inputObj);
                
                    for (k in outputJson.value) {
                        var expected = JSON.stringify(outputJson.value[k]);
                        var actual = JSON.stringify(outputObj[k]);
                        if (expected != actual) {
                            throw new Error("expected value not matching for field '" + k + "': " + expected + " != " + actual);
                        }
                    }
                } catch (e) {
                    console.error(e);
                    return;
                }
                console.info("Validation successful");
            },
            trigger_unit_tests: function trigger_unit_tests() {
                streamkap.start('unit_tests - this can take 30 sec up to a few minutes ... ');
            },
            unit_tests_results: function unit_tests_results() {
                streamkap.finish('unit_tests');
            },
            trigger_deploy_preview: function trigger_deploy_preview() {
                streamkap.start('deploy_preview');
            },
            check_deploy_preview_status: function check_deploy_preview_status() {
                streamkap.finish('deploy_preview');
            },
            trigger_poll_loop_for_output_topics: function trigger_poll_loop_for_output_topics() {
                streamkap.start('poll_output_topics');
            },
            list_output_topics: function list_output_topics() {
                streamkap.finish('poll_output_topics');
            },
            trigger_deploy_live: function trigger_deploy_live() {
                streamkap.start('deploy_live');
            },
            check_deploy_live_status: function check_deploy_live_status() {
                streamkap.finish('deploy_live');
            },
        };
    `;
    const py_code = ``; // TODO
    console.log('Loading utilities ', js_code, py_code);
    var kernel = null;
    while (!kernel) {
        console.log('Loading utilities for kernel ', kernel);
        kernel = await jupyterApp.commands.execute('notebook:get-kernel', { path: 'MAIN.ipynb' });
    
        if (waitForJupyterRetryCount > 25) {
            throw new Error('JupyterLite not loaded!');
        }
        waitForJupyterRetryCount++;
        await new Promise(resolve => setTimeout(resolve, 1000));
    }

    const code = kernel.name === 'javascript' ? js_code : py_code;
    console.log('Loading utilities, kernel ready ', kernel.name);
    await executeCodeInJupyterLiteKernel(kernel, 'Load streamkap utilities', js_code);
    
    var handleAsyncCallFromJupyterDebounced = debounce(handleAsyncCallFromJupyter, 500);

    kernel.iopubMessage.connect((_, msg) => {
        if (msg.header.msg_type === 'stream') {
            if (msg.content.name === 'stdout') {
                handleAsyncCallFromJupyterDebounced(kernel, msg);
            }
        }
    });

    await interceptOnSave(jupyterApp);
}

async function executeCodeInJupyterLiteKernel(kernel, description, js_code) {
    const future = kernel.requestExecute({ code: js_code });

    // Handle when the execution is done
    await future.done.then((res) => {
        if (res.content.status !== 'ok') {
            console.error('Error ' + description, res.content);
            return;
        }
        console.log(description + ' success');
    }).catch((error) => {
        console.error(description + ' failed:', error);
    });
}

async function handleAsyncCallFromJupyterResults(kernel, method, results_method, res) {
    var resTxt = res.replace(/"/g, '\\"').replace(/\n/g, '\\n');
    await executeCodeInJupyterLiteKernel(
        kernel, 
        `handleAsyncCallFromJupyter-${method}`, 
        `streamkap.append_msg("${method}", "${resTxt}")`);

    await auto_run_jupyter_cell(results_method);
    await auto_run_jupyter_cell(results_method);
}

async function handleAsyncCallFromJupyter(kernel, msg) {
    console.log('handleAsyncCallFromJupyter', msg);
    var res = "API call sent, waiting for response...";
    var method = null;
    var results_method = null;
    if (msg.content.text.match(/ \| in_out_topics/)) {
        method  = 'in_out_topics';
        results_method = 'check_in_out_topics';
    } else if (msg.content.text.match(/ \| unit_tests/)) {
        method = 'unit_tests';
        results_method = 'unit_tests_results';
    } else if (msg.content.text.match(/ \| deploy_preview/)) {
        method = 'deploy_preview';
        results_method = 'check_deploy_preview_status';
    } else if (msg.content.text.match(/ \| deploy_live/)) {
        method = 'deploy_live';
        results_method = 'streamkap.check_deploy_live_status';
    } else if (msg.content.text.match(/ \| poll_output_topics/)) {
        method = 'poll_output_topics';
        results_method = 'streamkap.list_output_topics';
    }


    var outputInterval = null;
    if (method) {
        handleAsyncCallFromJupyterResults(kernel, method, results_method, res);
        var outputInterval = setInterval(
            () => handleAsyncCallFromJupyterResults(kernel, method, results_method, res), 2500);
    }

    if (method == "in_out_topics") {
        res = await handle__in_out_topics(msg.content.text);
    } else if (method == "unit_tests") {
        res = await handle__unit_tests(msg.content.text);
    } else if (method == "deploy_preview") {
        res = await handle__deploy_preview(msg.content.text);
    } else if (method == "deploy_live") {
        res = await handle__deploy_live(msg.content.text);
    } else if (method == "poll_output_topics") {
        res = await handle__poll_output_topics(msg.content.text);
    }

    if (method) {
        clearInterval(outputInterval);
        handleAsyncCallFromJupyterResults(kernel, method, results_method, res);
    }
    
    console.log('handleAsyncCallFromJupyter', method, res);
}

async function handle__in_out_topics(msgText) {
    var params = msgText.split(" | ");
    console.log(handle__in_out_topics, params);
    var pattern = params[2];
    var replacement = params[3];
    console.log('in_out_topics', pattern, replacement);
    const res = await transformsStore.topicsForPattern(pattern, replacement);
    console.log('in_out_topics', res);
    var ret = "Topics:\n";
    for (var in_out_topic of res) {
        ret += `${in_out_topic[0]} --> ${in_out_topic[1]}\n`;
    }
    return ret;
}

async function auto_run_jupyter_cell(code_content) {
    //TODO: auto-run all the check* cells periodically so that the user does not have to do it manually
    console.log('auto_run_jupyter_cell', code_content);
    var jupyterApp = jupyterAppRef.value;

    // Assuming you have a reference to the notebook panel
    const notebookPanel = jupyterApp.shell.currentWidget;
    if (!notebookPanel) {
        console.error("No active notebook panel found!");
        return;
    }

    // The .content is the actual Notebook object
    const notebook = notebookPanel.content;

    // The cell widgets live in notebook.widgets[]
    var foundCell = -1;
    for (let i = 0; i < notebook.widgets.length; i++) {
        const cell = notebook.widgets[i];
        if (cell.model.type !== 'code') {
            continue; // Skip non-code cells
        }
        const source = cell.model.toJSON().source;

        // If it contains the search string, select that cell
        if (source.includes(code_content)) {
            notebook.activeCellIndex = i;
            foundCell = i;
            break;
        }
    }

    if (foundCell > -1) {
        await jupyterApp.commands.execute("notebook:run-cell", { activate: false });
        await jupyterApp.commands.execute("notebook:run-cell", { activate: false });
        console.log('auto_run_jupyter_cell done');
    } else {
        console.warn(`No cell found with content: ${code_content}`);
    }
}

async function handle__unit_tests(msgText) {
    var transformId = props.transformId;
    if (!implementation_details.value) {
        return "ERROR: transform implementation version not loaded correctly";
    }
    var versionId = implementation_details.value.version;
    console.log('unit_tests', transformId, versionId);
    const res = await transformsStore.runUnitTests(transformId, versionId);
    console.log('unit_tests', res);
    if (res.unit_test.is_success) {
        return "Unit tests passed!";
    } else {
        return "Unit tests failed!\n" + res.unit_test.text_diff;
    }
}

async function handle__deploy_preview(msgText) {
    var transformId = props.transformId;
    if (!implementation_details.value) {
        return "ERROR: transform implementation version not loaded correctly";
    }
    var versionId = implementation_details.value.version;
    console.log('deploy_preview', transformId, versionId);
    const res = await transformsStore.deployPreview(transformId, versionId);
    console.log('deploy_preview', res);
    if (res.transform_id == transformId) {
        return "Deploy successful!";
    } else {
        return "ERROR: Deploy failed!";
    }
}

async function handle__poll_output_topics(transformId) {
    var transformId = props.transformId;
    // const res = await transformsStore.listTopicsForTransform(transformId); //Topics page not showing transformed topics, dicover needs fixing
    console.log('poll_output_topics', transformId);
    const res = await transformsStore.topicsForPattern(`transform_${transformId}`, 'NOT-NEEDED');
    console.log('poll_output_topics', res);
    var ret = "Topics: \n";
    for (var in_out_topic of res) {
        ret += `${in_out_topic[0]}\n`;
    }

    return ret;
}

async function handle__deploy_live(msgText) {
    var transformId = props.transformId;
    if (!implementation_details.value) {
        return "ERROR: transform implementation version not loaded correctly";
    }
    var versionId = implementation_details.value.version;
    console.log('deploy_live', transformId, versionId);
    const res = await transformsStore.deployLive(transformId, versionId);
    console.log('deploy_live', res);
    if (res.transform_id == transformId) {
        return "Deploy successful!";
    } else {
        return "ERROR: Deploy failed!";
    }    
}


// Retrieves all files from a given directory (recursively).
async function getAllFiles(docManager, path = '') {
    try {
        const directory = await docManager.get(path, { content: true });
        const files = [];

        if (directory.type === 'directory') {
            for (const item of directory.content) {
                if (item.type === 'directory') {
                    const subFiles = await getAllFiles(docManager, item.path);
                    files.push(...subFiles);
                } else if (item.type === 'file' || item.type === 'notebook' || item.type === 'json') {
                    console.log('Item:', item);
                    var itemPath = item.path;
                    var content = item.content;
                    if (item.name === 'MAIN.ipynb') {
                        itemPath = 'main_notebook';
                    } else if (item.name.match(/transform_code.*Transform.js$/)) {
                        itemPath = item.name.replace(/Transform.js$/, '_transform');
                    } else if (item.name.match(/\.json$/)) {
                        if (item.name.match(/unit_test.*\.json$/)) {
                            itemPath = item.name.replace(/\.json.js$/, '__json');
                        }
                        try {
                            content = JSON.parse(content);
                        } catch (e) {
                            console.error('Error parsing JSON:', e);
                        }
                    }

                    files.push({ path: itemPath, content: content });
                }
            }
        }

        return files;
    } catch (error) {
        console.error(`Error listing files in path "${path}":`, error);
        return [];
    }
}

// Saves all files by collecting them from JupyterLite and then sending them to the server.
async function saveAllFiles(docManager) {
    message.value = 'Saving all files...';

    try {
        const allFiles = await getAllFiles(docManager);
        console.log('All files:', allFiles);

        var mandatoryFilePrefixes = {
            main_notebook: false,
            'transform_code/valueTransform': false
        };
        const impl_to_save = cloneDeep(implementation_details.value);
        for (const file of allFiles) {
            for (const prefix in mandatoryFilePrefixes) {
                if (file.path.startsWith(prefix)) {
                    mandatoryFilePrefixes[prefix] = true;
                }
            }

            const filePath = file.path;
            let jsonPath = filePath.replace(/\//g, '.').replace(/(\w+\.(?:json|js|py|sql|txt|csv|ipynb))$/, '["$1"]');
            const fileContent = file.content;

            deepSet(impl_to_save, jsonPath, fileContent);
        }

        for (const prefix in mandatoryFilePrefixes) {
            if (mandatoryFilePrefixes[prefix] == false) {
                message.value = `Missing mandatory file to save: ${prefix}`;
                throw new Error(message.value);
            }
        }

        // New version datetime YYMMDDHH
        const newVersion = new Date().toISOString().slice(2, 19).replace(/[-:T]/g, '').slice(0, 8);
        if (impl_to_save.version !== newVersion) {
            console.log('New version:', newVersion, ", prev version", impl_to_save.version);
            if (newVersion > impl_to_save.version) {
                impl_to_save.version = newVersion;
            }
            console.log("Saving version", impl_to_save.version);
        }

        message.value = `Saving files: ${allFiles.map((f) => f.path).join(', ')}`;
        console.log('Saving files:', impl_to_save);

        const savedImplDetails = await transformsStore.editImplementationDetails(impl_to_save._id, impl_to_save);
        message.value = `Saved files: ${allFiles.map((f) => f.path).join(', ')}`;
        console.log('Saving files success:', savedImplDetails);
    } catch (error) {
        console.error('Error saving files:', error);
        message.value = 'Failed to save files!';
    }
}

onMounted(() => {
    jupyterLoading.value = true;
    reloadFiles();
    saveAllFilesDebounced = debounce(saveAllFiles, 1000);
    setTimeout(() => applyStreamkapCustomSettings(), 1000);
});
</script>

<style scoped></style>
